import { createEffect, untrack } from "solid-js";
import { debug } from "./console";
import { eventType, filtered, maneuvers } from "../store/globalStore";
import { setSelectedGradesManeuvers } from "../store/filterStore";
import { lastMapManeuverClick } from "./mapManeuverClick";
import { decideMapClickGrade, MANEUVER_GRADE_ALL } from "./maneuverGradeFilter";

export interface OpenGradeAllForMapClickActions {
  /** True once the page has applied its own grade default, so a click is not eaten by mount. */
  ready: () => boolean;
  grades: () => number[];
  setSelectedGrades: (grades: number[]) => void;
  /** Refetch. Grade is applied on the server, so a client refilter cannot bring back a hidden grade. */
  reload: () => void;
}

/**
 * When a map marker click is outside the maneuvers currently on screen, select the All grade
 * and reload. Other filters stay as they are.
 */
export function useOpenGradeAllForMapClick(actions: OpenGradeAllForMapClickActions): void {
  let handledTick = untrack(() => lastMapManeuverClick()?.tick ?? 0);

  createEffect(() => {
    const click = lastMapManeuverClick();
    const ready = actions.ready();
    const grades = actions.grades();
    const rows = maneuvers();
    const shown = filtered();
    const pageType = eventType();
    if (!ready || click == null) return;
    if (click.tick === handledTick) return;

    const rowList = Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
    const shownIds = Array.isArray(shown) ? shown.map((id) => Number(id)) : [];
    const decision = decideMapClickGrade({
      eventId: click.eventId,
      eventType: click.eventType || pageType || "",
      grades,
      allRows: rowList,
      shownIds,
    });
    if (decision === "wait") return;

    handledTick = click.tick;
    if (decision !== "open-all") return;

    untrack(() => {
      debug("Map click is outside the maneuver grade filter; setting grade to All", {
        eventId: click.eventId,
      });
      actions.setSelectedGrades([MANEUVER_GRADE_ALL]);
      setSelectedGradesManeuvers([String(MANEUVER_GRADE_ALL)]);
      actions.reload();
    });
  });
}
